import CloseIcon from "@mui/icons-material/Close";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from "react";
import { scrollbarStyle } from "../../../shared/dashboard";
import EmailType from "../../molecules/home-manage-fields/EmailType";
import Enrichment from "../../molecules/home-manage-fields/Enrichment";
import NewEmail from "../../molecules/home-manage-fields/NewEmail";
import Sync from "../../molecules/home-manage-fields/Sync";
import { ReactComponent as WorkflowIcon } from "../../../assets/icons/workflow-2.svg";

const useStyles = makeStyles((theme) => ({
    dialog: {
        "& .MuiDialog-paper": {
            minWidth: 1450,
            width: '100%',
            minHeight: 800,
            height: 'fit-content',
            borderRadius: 24,
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: '#fff',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.75rem',
        background: 'linear-gradient(135deg, #1eb6a8 0%, #18a598 100%)',
        padding: '32px 40px',
        position: 'relative',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '8px 0',
        height: '50vh',
        overflowY: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '24px 40px',
        background: '#f8f9fc',
    },
    headerIcon: {
        width: 64,
        height: 64,
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: 16,
        fontSize: 32,
    },
    closeIcon: {
        position: 'absolute',
        top: '24px',
        right: '24px',
        width: 40,
        height: 40,
        padding: 'unset',
        borderRadius: 8,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        "& > .MuiSvgIcon-root": {
            fill: '#fff'

        },
    },
    fields: {
        height: '100%',
        padding: '8px 12px 60px 8px',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    card: {
        position: 'relative',
        width: 300,
        height: 'fit-content',
        minHeight: 80,
        padding: '12px 16px',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `2px solid ${theme.colors.neutral['400']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2)',
        transition: 'all 300ms ease-in-out',
        "&:hover": {
            boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        },
    },
    checkBox: {
        padding: 'unset !important',
        marginRight: '4px !important',
        "& > svg": {
            width: '24px !important',
            height: '24px !important',
        }
    },
    icon: {
        padding: 0,
        "& > svg": {
            width: '28px !important',
            height: '28px !important'
        },
    },
    icon2: {
        width: '28px !important',
        height: '28px !important',
        fill: theme.colors.neutral['400'],
    },
    field: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    cancelBtn: {
        width: 109,
        height: 45,
        background: 'white',
        color:'#64748b',
        border: '2px solid #e2e8f0',
        borderRadius: 10,
        textTransform: 'none',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: '600',
        transition: 'ease-in-out 300ms',
    },
    saveBtn: {
        width: 162,
        height: 45,
        borderRadius: 10,
        background: 'linear-gradient(135deg, #1eb6a8 0%, #18a598 100%)',
        color: 'white',
        textTransform: 'none',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: '600',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            backgroundColor: theme.colors.primary['500'],
        }
    },
}));

const BuildWorkflowModal: React.FC<{
    open: boolean,
    onOpenModal?: (field?: string) => void,
    onClose: () => void,
}> = ({ open, onOpenModal, onClose }) => {
    const classes = useStyles();
    const [emailType, setEmailType] = useState<string>('Company Update');

    return (<>
        <Dialog className={classes.dialog} open={open} onClose={() => onClose()}>
            <DialogTitle className={classes.dialogTitle}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                    <Stack className={classes.headerIcon} alignItems="center" justifyContent="center"
                        sx={{ 'svg > path': { fill: '#fff' }}}>
                        <WorkflowIcon style={{ width: 40, height: 40 }} />
                    </Stack>
                    <Stack direction="column" spacing={1} alignItems="flex-start">
                        <Typography fontFamily="Inter" fontSize="1.5rem" fontWeight="bold">
                            {'Build Your Ideal Workflow'}
                        </Typography>
                        <Typography fontFamily="Lato" fontSize="1rem" fontWeight="400" color="rgba(255, 255, 255, 0.9)">
                            {'Customize how data flows from your email to your tools'}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    <IconButton className={classes.closeIcon}
                        onClick={onClose} disableRipple
                        sx={{ padding: 'unset !important'}}>
                        <CloseIcon sx={{ width: 20, height: 20, fill: 'white' }} />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Divider sx={{ width: '100%' }}/>
            <DialogContent className={classes.dialogContent}>
                <Stack direction="row" alignItems="center" justifyContent="center"
                    sx={{
                        width: '100%', height: '100%', paddingLeft: 4, paddingRight: 2,
                        backgroundImage: 'radial-gradient(rgba(0,0,0,0.15) 2px, transparent 2px)',
                        backgroundSize: '25px 25px',
                    }}>
                    <NewEmail />
                    <EmailType onEmailType={setEmailType} />
                    <Enrichment emailType={emailType} onOpenModal={onOpenModal} />
                    <Sync />
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions className={classes.dialogActions}>
                <Button className={classes.cancelBtn} onClick={onClose}> {'Cancel'} </Button>
                <Button variant="contained" className={classes.saveBtn} onClick={onClose}> {'Save Workflow'} </Button>
            </DialogActions>
        </Dialog>
    </>);
}

export default BuildWorkflowModal;
